import { useCallback, useState } from 'react';

declare global {
  interface Window {
    grecaptcha: any;
    RECAPTCHA_SITE_KEY: string;
    loadRecaptcha: () => Promise<void>;
    recaptchaLoaded: boolean;
  }
}

export type RecaptchaResult = 'success' | 'failed' | 'timeout' | 'not_ready';

/**
 * Load reCAPTCHA script on-demand and wait for it to be ready
 */
const loadAndWaitForRecaptcha = (timeout = 8000): Promise<boolean> => {
  return new Promise((resolve) => {
    // Trigger on-demand loading
    if (window.loadRecaptcha) {
      window.loadRecaptcha();
    }
    
    const startTime = Date.now();
    
    const check = () => {
      const grecaptcha = window.grecaptcha?.enterprise;
      
      if (grecaptcha && typeof grecaptcha.ready === 'function') {
        grecaptcha.ready(() => {
          console.log('[useRecaptcha] ✅ reCAPTCHA Enterprise fully loaded');
          resolve(true);
        });
        return;
      }
      
      if (Date.now() - startTime > timeout) {
        console.warn('[useRecaptcha] ⚠️ reCAPTCHA load timeout after', timeout, 'ms');
        resolve(false);
        return;
      }
      
      setTimeout(check, 100);
    };
    
    check();
  });
};

/**
 * reCAPTCHA Enterprise hook with invisible check first.
 * Loads script on-demand to reduce initial page load JS execution.
 */
export const useRecaptcha = () => {
  const [isReady, setIsReady] = useState(false);

  /**
   * Execute invisible reCAPTCHA check first, only request visible challenge if suspicious
   */
  const executeRecaptcha = useCallback(async (): Promise<RecaptchaResult> => {
    // Load reCAPTCHA on-demand when first needed
    if (!isReady) {
      console.log('[useRecaptcha] ⏳ Loading reCAPTCHA on-demand...');
      const loaded = await loadAndWaitForRecaptcha(8000);
      if (loaded) {
        setIsReady(true);
      } else {
        console.warn('[useRecaptcha] ⚠️ reCAPTCHA failed to load - allowing through');
        return 'not_ready';
      }
    }

    const grecaptcha = window.grecaptcha?.enterprise;
    
    if (!grecaptcha) {
      console.warn('[useRecaptcha] ⚠️ reCAPTCHA Enterprise not available - allowing through');
      return 'not_ready';
    }

    if (!window.RECAPTCHA_SITE_KEY) {
      console.warn('[useRecaptcha] ⚠️ RECAPTCHA_SITE_KEY not set - allowing through');
      return 'not_ready';
    }

    return new Promise((resolve) => {
      grecaptcha.ready(async () => {
        try {
          console.log('[useRecaptcha] 🔄 Executing invisible reCAPTCHA check...');
          
          const token = await grecaptcha.execute(window.RECAPTCHA_SITE_KEY, { 
            action: 'submit' 
          });

          if (!token) {
            console.warn('[useRecaptcha] ⚠️ No token received - allowing through');
            resolve('not_ready');
            return;
          }

          console.log('[useRecaptcha] 📤 Sending token for server verification...');

          // Send to server for verification
          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-recaptcha`,
            {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
              },
              body: JSON.stringify({ token, action: 'submit' }),
            }
          );

          const result = await response.json();
          console.log('[useRecaptcha] 📥 Server response:', result);

          if (result.success) {
            console.log('[useRecaptcha] ✅ Passed invisible check (score:', result.score, ')');
            resolve('success');
          } else if (result.requiresChallenge) {
            console.log('[useRecaptcha] ⚠️ Suspicious score - showing visible challenge');
            resolve('timeout');
          } else {
            console.warn('[useRecaptcha] ❌ Failed verification:', result.error, result.reason);
            // If BROWSER_ERROR, it's likely a loading issue - allow through
            if (result.reason === 'BROWSER_ERROR') {
              console.warn('[useRecaptcha] ⚠️ Browser error - allowing through');
              resolve('not_ready');
            } else {
              resolve('failed');
            }
          }
        } catch (error) {
          console.error('[useRecaptcha] ❌ Error during verification:', error);
          // Fail-open on network errors to not block legitimate users
          resolve('not_ready');
        }
      });
    });
  }, [isReady]);

  /**
   * Legacy method for backward compatibility
   */
  const executeRecaptchaLegacy = useCallback(async (): Promise<boolean> => {
    const result = await executeRecaptcha();
    return result === 'success' || result === 'not_ready';
  }, [executeRecaptcha]);

  return { 
    executeRecaptcha, 
    executeRecaptchaLegacy,
    isReady 
  };
};
